import React, { FC, useState, useEffect } from 'react';
import { snakeToCamel } from '../../caseUtils';
import {
  CustomWindow,
  LawFirmContextType,
  LawFirmExperiences,
} from './lawFirm.types';
import { flattenDomains } from '../../entity/expertises/entityExpertises.utils';
import { buildUrlWithParameters } from '../../utils/url-utils/urlUtils';
import { getPageName } from '../../utils/pageName';
import { fetchLawFirmDecisions } from './lawFirm.api';

declare let window: CustomWindow;

export const LawFirmContext = React.createContext<LawFirmContextType>({
  experience: { decisionCount: 0, domains: [], juridictions: [], villes: [] },
  lawFirmInfos: snakeToCamel(window.law_firm_info),
  allDecisionsLink: '',
});

export const LawFirmProvider: FC = ({ children }) => {
  const lawFirmInfos = snakeToCamel(window.law_firm_info);
  const [experience, setExperience] = useState<LawFirmExperiences>({
    decisionCount: 0,
    domains: [],
    juridictions: [],
    villes: [],
  });

  useEffect(() => {
    async function load() {
      const {
        domains,
        juridictions,
        villes,
        decisionCount,
      } = await fetchLawFirmDecisions(lawFirmInfos.lawOfficeId);
      setExperience({
        domains: flattenDomains(domains),
        juridictions,
        villes,
        decisionCount,
      });
    }
    load();
  }, [lawFirmInfos.lawOfficeId]);

  const allDecisionsLink = buildUrlWithParameters('/search', {
    q: encodeURIComponent(lawFirmInfos.name),
    chrono: true,
    oid: lawFirmInfos.lawOfficeIdGroup,
    sourcePage: getPageName(),
  });

  return (
    <LawFirmContext.Provider
      value={{
        experience,
        lawFirmInfos,
        allDecisionsLink,
      }}
    >
      {children}
    </LawFirmContext.Provider>
  );
};

export const withLawFirm = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const ComponentWithLawyer: FC<Pick<
    P,
    Exclude<keyof P, keyof LawFirmContextType>
  >> = (props) => (
    <LawFirmContext.Consumer>
      {(lawFirm: LawFirmContextType) => (
        <Component {...(props as P)} {...lawFirm} />
      )}
    </LawFirmContext.Consumer>
  );
  return ComponentWithLawyer;
};
